import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  listTaskImports,
  fetchTaskImport,
  remapTaskImportRow,
  setTaskImportFlight,
  commitTaskImport,
  discardTaskImport,
  listAvatars,
  listPainPoints,
  listAngleChoices,
  listHookTypeChoices,
  listAdTypeChoices,
  listStyleChoices,
  listDesignerProfiles,
  listDesignerGroups,
  listFlights,
} from '@src/api/creativeStudio';
import { fetchVerticals } from '@src/api/actions/verticals';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: '55+', label: '55+' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
];

// Creative-dimension fields shown in the expanded detail row.
const DIM_FIELDS = [
  { key: 'avatar', label: 'Avatar', scoped: true },
  { key: 'pain_point', label: 'Pain Point', scoped: true },
  { key: 'angle', label: 'Angle' },
  { key: 'hook_type', label: 'Hook Type' },
  { key: 'ad_type', label: 'Ad Type' },
  { key: 'style', label: 'Style' },
];

const errMsg = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.error || fallback;

const arr = (res) => (Array.isArray(res?.data) ? res.data : []);

// A small status glyph for a field given its match_status entry.
function MatchGlyph({ status }) {
  if (status === 'matched') {
    return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
  }
  if (status === 'unmatched' || status === 'ambiguous') {
    return (
      <Tooltip title={status === 'ambiguous' ? 'Multiple matches — pick one' : "Couldn't match — pick one"}>
        <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
      </Tooltip>
    );
  }
  return null;
}

// All pending pushes are reviewed in ONE flat table — external systems push a
// few ads at a time, so per-push navigation is friction. Each row keeps a
// reference to its batch for remap/commit; "Commit" commits every fully-mapped
// push in one click.
export default function TaskImportReviewModal({ open, onClose, onCommitted }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [remember, setRemember] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [opts, setOpts] = useState({
    verticals: [], avatars: [], painPoints: [], angles: [],
    hookTypes: [], adTypes: [], styles: [], designers: [], designerGroups: [], flights: [],
  });

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
  } = useClientAutocomplete();

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listTaskImports({ status: 'pending' });
      const details = await Promise.all(arr(res).map((b) => fetchTaskImport(b.id)));
      setBatches(details.map((d) => d.data));
    } catch (err) {
      setError(errMsg(err, 'Failed to load pending imports'));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshBatch = useCallback(async (batchId) => {
    try {
      const res = await fetchTaskImport(batchId);
      setBatches((prev) => prev.map((b) => (b.id === batchId ? res.data : b)));
    } catch (err) {
      setError(errMsg(err, 'Failed to refresh import'));
    }
  }, []);

  // Load option lists + pending batches when the modal opens.
  useEffect(() => {
    if (!open) return;
    loadAll();
    (async () => {
      try {
        const [v, av, pp, an, ht, at, st, de, dg, fl] = await Promise.all([
          fetchVerticals(), listAvatars(), listPainPoints(), listAngleChoices(),
          listHookTypeChoices(), listAdTypeChoices(), listStyleChoices(),
          listDesignerProfiles(), listDesignerGroups(), listFlights(),
        ]);
        setOpts({
          verticals: arr(v), avatars: arr(av), painPoints: arr(pp), angles: arr(an),
          hookTypes: arr(ht), adTypes: arr(at), styles: arr(st),
          designers: arr(de), designerGroups: arr(dg), flights: arr(fl),
        });
      } catch (_e) { /* keep usable */ }
    })();
  }, [open, loadAll]);

  const remap = useCallback(async (batchId, row, payload) => {
    setSavingRowId(row.id);
    setError('');
    try {
      await remapTaskImportRow(batchId, row.id, { ...payload, save_aliases: remember });
      await refreshBatch(batchId);
    } catch (err) {
      setError(errMsg(err, 'Remap failed'));
    } finally {
      setSavingRowId(null);
    }
  }, [remember, refreshBatch]);

  const handleSetFlight = useCallback(async (batchId, flightId) => {
    setBusy(true);
    setError('');
    try {
      await setTaskImportFlight(batchId, flightId || '');
      await refreshBatch(batchId);
    } catch (err) {
      setError(errMsg(err, 'Could not set flight'));
    } finally {
      setBusy(false);
    }
  }, [refreshBatch]);

  const readyBatches = batches.filter((b) => b.is_clean);

  const handleCommit = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const results = [];
      for (const b of readyBatches) {
        const res = await commitTaskImport(b.id);
        results.push(res.data);
      }
      onCommitted?.({
        created: results.reduce((n, r) => n + (r.created || 0), 0),
        assigned: results.reduce((n, r) => n + (r.assigned || 0), 0),
      });
      const remaining = batches.filter((b) => !b.is_clean);
      setBatches(remaining);
      if (!remaining.length) onClose?.();
    } catch (err) {
      setError(errMsg(err, 'Commit failed — some rows still need mapping'));
      loadAll();
    } finally {
      setBusy(false);
    }
  }, [batches, readyBatches, onCommitted, onClose, loadAll]);

  const handleDiscardAll = useCallback(async () => {
    const label = batches.length > 1 ? `all ${batches.length} pending pushes` : 'this import';
    if (!window.confirm(`Discard ${label}? The staged tasks will not be created.`)) return;
    setBusy(true);
    try {
      for (const b of batches) await discardTaskImport(b.id);
      setBatches([]);
      onClose?.();
    } catch (err) {
      setError(errMsg(err, 'Discard failed'));
      loadAll();
    } finally {
      setBusy(false);
    }
  }, [batches, onClose, loadAll]);

  const dimOptions = (key, row) => {
    const vId = row.resolved.vertical?.id;
    switch (key) {
      case 'angle': return opts.angles.map((o) => ({ id: o.id, label: o.label }));
      case 'hook_type': return opts.hookTypes.map((o) => ({ id: o.id, label: o.label }));
      case 'ad_type': return opts.adTypes.map((o) => ({ id: o.id, label: o.label }));
      case 'style': return opts.styles.map((o) => ({ id: o.id, label: o.label }));
      case 'avatar':
        return opts.avatars.filter((o) => o.vertical === vId).map((o) => ({ id: o.id, label: o.name }));
      case 'pain_point':
        return opts.painPoints.filter((o) => o.vertical === vId).map((o) => ({ id: o.id, label: o.label }));
      default: return [];
    }
  };

  // Flatten every pending push into one table; each row keeps its batch.
  const allRows = batches.flatMap((b) => b.rows.map((row) => ({ batch: b, row })));
  const totalTasks = allRows.filter(({ row }) => !row.excluded).length;
  const needsAttention = batches.reduce((n, b) => n + (b.needs_attention_count || 0), 0);
  const readyTasks = readyBatches.reduce(
    (n, b) => n + b.rows.filter((r) => !r.excluded).length, 0,
  );
  // Pushes whose flight reference didn't resolve (and wasn't dismissed).
  const flightProblems = batches.filter((b) => b.flight_raw && !b.flight && !b.flight_cleared);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <span>Review imported tasks</span>
          {batches.length > 1 ? (
            <Typography variant="body2" color="text.secondary">
              {batches.length} pushes · {totalTasks} task(s)
            </Typography>
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

        {loading ? (
          <Stack alignItems="center" py={6}><CircularProgress /></Stack>
        ) : !batches.length ? (
          <Typography color="text.secondary" align="center" py={6}>No imports waiting for review.</Typography>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Source: <strong>{[...new Set(batches.map((b) => b.source || 'api'))].join(', ')}</strong>
                {' · '}{totalTasks} task(s)
              </Typography>
              {needsAttention ? (
                <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={`${needsAttention} need mapping`} />
              ) : (
                <Chip size="small" color="success" icon={<CheckCircleIcon />} label="All mapped — ready to commit" />
              )}
              <Box flexGrow={1} />
              <FormControlLabel
                control={<Switch size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
                label="Remember my mappings for next time"
              />
            </Stack>

            {/* Per-push flight mapping (only if a flight name was sent but not resolved). */}
            {flightProblems.map((b) => (
              <Alert key={b.id} severity="warning" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <span>Flight “{b.flight_raw}” (push #{b.id}) didn’t match. Pick one:</span>
                  <TextField
                    size="small" select sx={{ minWidth: 220 }} value="" disabled={busy}
                    onChange={(e) => handleSetFlight(b.id, e.target.value === '__none__' ? null : e.target.value)}
                  >
                    <MenuItem value="__none__"><em>No flight</em></MenuItem>
                    {opts.flights.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.flight_code} · {f.name}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Alert>
            ))}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={36} />
                  <TableCell>#</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Vertical</TableCell>
                  <TableCell>Designer</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Skip</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allRows.map(({ batch, row }, flatIndex) => {
                  const saving = savingRowId === row.id;
                  const open_ = !!expanded[row.id];
                  const ms = row.match_status || {};
                  const clientValue = row.resolved.client
                    ? { value: row.resolved.client.id, label: row.resolved.client.label }
                    : null;
                  return (
                    <React.Fragment key={`${batch.id}-${row.id}`}>
                      <TableRow sx={{ opacity: row.excluded ? 0.4 : 1, '& td': { borderBottom: open_ ? 'none' : undefined } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => setExpanded((p) => ({ ...p, [row.id]: !open_ }))}>
                            {open_ ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <span>{flatIndex + 1}</span>
                            {batches.length > 1 ? (
                              <Tooltip title={`Push #${batch.id} · ${batch.source || 'api'}`}>
                                <Chip size="small" variant="outlined" label={`#${batch.id}`} sx={{ height: 18, fontSize: 11 }} />
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>

                        {/* Client (searchable) */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.client} />
                            <Autocomplete
                              size="small" sx={{ minWidth: 200 }} disabled={saving || row.excluded}
                              options={clientOptions} loading={clientOptionsLoading} value={clientValue}
                              getOptionLabel={(o) => o.label || ''}
                              isOptionEqualToValue={(o, v) => o.value === v.value}
                              onInputChange={(_e, v) => handleClientSearch(v)}
                              onChange={(_e, v) => v && remap(batch.id, row, { client: v.value })}
                              renderInput={(params) => (
                                <TextField {...params} placeholder={row.raw?.client || 'Pick client'} variant="standard" />
                              )}
                            />
                          </Stack>
                        </TableCell>

                        {/* Vertical */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.vertical} />
                            <TextField
                              size="small" select variant="standard" sx={{ minWidth: 120 }}
                              disabled={saving || row.excluded}
                              value={row.resolved.vertical?.id || ''}
                              onChange={(e) => remap(batch.id, row, { vertical: e.target.value || null })}
                            >
                              <MenuItem value=""><em>{row.raw?.vertical || '—'}</em></MenuItem>
                              {opts.verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                            </TextField>
                          </Stack>
                        </TableCell>

                        {/* Designer */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.designer} />
                            <TextField
                              size="small" select variant="standard" sx={{ minWidth: 130 }}
                              disabled={saving || row.excluded}
                              value={row.resolved.designer?.id || ''}
                              onChange={(e) => remap(batch.id, row, { designer: e.target.value || null })}
                            >
                              <MenuItem value=""><em>{row.raw?.designer || 'Unassigned'}</em></MenuItem>
                              {opts.designers.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                        </TableCell>

                        {/* Audience */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.audience} />
                            <TextField
                              size="small" select variant="standard" sx={{ minWidth: 110 }}
                              disabled={saving || row.excluded}
                              value={row.audience || ''}
                              onChange={(e) => remap(batch.id, row, { audience: e.target.value })}
                            >
                              <MenuItem value=""><em>{row.raw?.audience || '—'}</em></MenuItem>
                              {AUDIENCE_OPTIONS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                            </TextField>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {saving ? <CircularProgress size={16} /> : row.is_clean
                            ? <Chip size="small" color="success" variant="outlined" label="ready" />
                            : <Chip size="small" color="warning" variant="outlined" label="needs mapping" />}
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title={row.excluded ? 'Include this row' : 'Skip this row on commit'}>
                            <IconButton
                              size="small" color={row.excluded ? 'default' : 'error'} disabled={saving}
                              onClick={() => remap(batch.id, row, { excluded: !row.excluded })}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail: creative dimensions + script preview */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open_ ? undefined : 'none' }}>
                          <Collapse in={open_} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, pl: 5 }}>
                              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                {DIM_FIELDS.map((f) => {
                                  const options = dimOptions(f.key, row);
                                  const scopedDisabled = f.scoped && !row.resolved.vertical;
                                  return (
                                    <Stack key={f.key} direction="row" spacing={0.5} alignItems="center">
                                      <MatchGlyph status={ms[f.key]} />
                                      <TextField
                                        size="small" select label={f.label} sx={{ minWidth: 150 }}
                                        disabled={saving || row.excluded || scopedDisabled}
                                        helperText={scopedDisabled ? 'set vertical first' : (row.raw?.[f.key] || '')}
                                        value={row.resolved[f.key]?.id || ''}
                                        onChange={(e) => remap(batch.id, row, { [f.key]: e.target.value || null })}
                                      >
                                        <MenuItem value=""><em>—</em></MenuItem>
                                        {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                                      </TextField>
                                    </Stack>
                                  );
                                })}
                              </Stack>

                              {row.hook_text ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                  <strong>Hook:</strong> {row.hook_text}
                                </Typography>
                              ) : null}
                              {row.script_text ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                  <strong>Script{row.is_custom_script ? ' (custom)' : ''}:</strong>{' '}
                                  {row.script_text.length > 280 ? `${row.script_text.slice(0, 280)}…` : row.script_text}
                                </Typography>
                              ) : null}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button color="error" onClick={handleDiscardAll} disabled={!batches.length || busy}>
          {batches.length > 1 ? 'Discard all' : 'Discard'}
        </Button>
        <Box flexGrow={1} />
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button
          variant="contained" onClick={handleCommit}
          disabled={!readyBatches.length || busy}
        >
          {busy ? <CircularProgress size={18} /> : `Commit ${readyTasks} task(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
